import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ClipboardList, 
  ArrowLeft, 
  MessageSquare,
  User,
  Bot,
  Shield,
  Clock,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Markdown renderer
const MarkdownContent = ({ content }) => {
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p className="mb-2 leading-relaxed text-neutral-700 text-sm">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold text-neutral-900">{children}</strong>,
        code: ({ inline, children }) => 
          inline 
            ? <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-xs font-mono text-primary-600">{children}</code>
            : <code className="block bg-neutral-50 p-2 rounded text-xs font-mono overflow-x-auto mb-2">{children}</code>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

const AdminReviewLogs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/create');
      return;
    }
    fetchConversations();
  }, [user, navigate]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/review-agents/conversations-with-review`);
      setConversations(response.data);
    } catch (error) {
      console.error('獲取對話列表失敗:', error);
      toast.error('獲取對話列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadConversationDetail = async (conversationId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/review-agents/conversation-detail/${conversationId}`);
      setSelectedConversation(response.data);
    } catch (error) {
      console.error('獲取對話詳情失敗:', error);
      toast.error('獲取對話詳情失敗');
    } finally {
      setLoadingDetail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-neutral-100 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/review-agents')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回審核 Agent
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-heading font-bold text-neutral-900">審核對話記錄</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
        {/* Info Banner */}
        <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">審核對話記錄說明</h3>
              <p className="text-sm text-blue-700 mt-1">
                此頁面顯示所有啟用了 Ultra 審核功能的對話。您可以查看完整的對話記錄，包括 Main Agent 和 Review Agent 之間的交互。
              </p>
            </div>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Conversation List */}
          <div className="space-y-4">
            <h2 className="text-lg font-heading font-bold text-neutral-900">對話列表</h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-neutral-600">載入中...</p>
              </div>
            ) : conversations.length === 0 ? (
              <Card className="p-12 text-center">
                <MessageSquare className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">尚無審核對話</h3>
                <p className="text-neutral-600">
                  當用戶使用啟用了審核功能的 Agent 時，對話記錄會顯示在此處
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {conversations.map((conv) => (
                  <Card 
                    key={conv.id} 
                    className={`p-4 cursor-pointer transition-all hover:shadow-md ${selectedConversation?.id === conv.id ? 'border-primary-500 bg-primary-50' : ''}`}
                    onClick={() => loadConversationDetail(conv.id)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-primary-600" />
                        <span className="font-medium text-neutral-900">{conv.agent_name}</span>
                      </div>
                      <span className="text-xs text-neutral-500">{formatDate(conv.updated_at)}</span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-neutral-600">
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {conv.user_email}
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {conv.messages?.length || 0} 條消息
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Right: Conversation Detail */}
          <div>
            <h2 className="text-lg font-heading font-bold text-neutral-900 mb-4">對話詳情</h2>
            
            {loadingDetail ? (
              <Card className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-neutral-600">載入詳情中...</p>
              </Card>
            ) : !selectedConversation ? (
              <Card className="p-12 text-center">
                <Eye className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">請選擇一個對話</h3>
                <p className="text-neutral-600">
                  點擊左側的對話以查看完整的審核記錄
                </p>
              </Card>
            ) : (
              <Card className="p-6">
                {/* Conversation Info */}
                <div className="mb-4 pb-4 border-b">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{selectedConversation.agent_icon}</span>
                    <div>
                      <h3 className="font-semibold text-neutral-900">{selectedConversation.agent_name}</h3>
                      <p className="text-xs text-neutral-500">{selectedConversation.user_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-neutral-600">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(selectedConversation.created_at)}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {selectedConversation.messages?.map((msg, index) => (
                    <div key={index} className={`p-3 rounded-lg ${
                      msg.role === 'user' 
                        ? 'bg-blue-50 border border-blue-200' 
                        : msg.internal 
                          ? 'bg-amber-50 border border-amber-300 border-dashed' 
                          : 'bg-neutral-50 border border-neutral-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {msg.role === 'user' ? (
                          msg.internal ? (
                            <>
                              <Shield className="w-4 h-4 text-amber-600" />
                              <span className="text-xs font-medium text-amber-900">Review Agent 反饋</span>
                              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">內部</span>
                            </>
                          ) : (
                            <>
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-medium text-blue-900">用戶</span>
                            </>
                          )
                        ) : (
                          <>
                            <Bot className="w-4 h-4 text-neutral-600" />
                            <span className="text-xs font-medium text-neutral-900">
                              {msg.internal ? 'Main Agent (待審核內容)' : 'Main Agent'}
                            </span>
                            {msg.review_enabled && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Ultra</span>
                            )}
                            {msg.internal && (
                              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">內部</span>
                            )}
                          </>
                        )}
                        <span className="text-xs text-neutral-500 ml-auto">
                          {new Date(msg.timestamp).toLocaleTimeString('zh-TW')}
                        </span>
                      </div>
                      
                      <div className="text-sm text-neutral-700 whitespace-pre-wrap">
                        <MarkdownContent content={msg.content} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t text-xs text-neutral-500">
                  <p>💡 提示：</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>藍色框：用戶消息</li>
                    <li>灰色框：Main Agent 回覆（發送給用戶）</li>
                    <li>橙色虛線框：內部審核交互（Main Agent ↔ Review Agent，用戶不可見）</li>
                  </ul>
                </div>
              </Card>
            )}
          </div>
        </div>
        </div>
      </main>
    </div>
  );
};

export default AdminReviewLogs;
